import {
    FetchState,
    CommunicationState,
    CommunicationResponse,
    HsabPlanType,
    CommunicationDashboard
} from "@/shared/types";
import { PartnerEmployerList } from "@/employer/types";
import * as UserService from "@/shared/api/userService";
import * as storeHelper from "@/shared/storeHelper";
import { isMobileApp } from "@/shared/mobileHelper";
import { defineStore } from "pinia";
import _ from "lodash";

export const initialState: CommunicationState = {
    fetchState: FetchState.Uninitialized,
    records: [],
    previewFetchState: FetchState.Uninitialized,
    previewRecord: null,
    partnerEmployerList: null,
    partnerEmployerListFetchState: FetchState.Uninitialized
};

export const useCommunicationStore = defineStore("communication", {
    state: (): CommunicationState => (_.cloneDeep(initialState)),
    getters: {
        hasCommunications: state => state.fetchState === FetchState.Loaded && state.records.length > 0,
        communications: state => state.records,
        isPreviewRecordLoaded () {
          return (id: number) => this.previewFetchState === FetchState.Loaded &&
              this.previewRecord.id === id;
        },
        isPreviewRecordLoading: state => state.previewFetchState === FetchState.Loading,
        previewRecords: state => [ state.previewRecord ],
    },
    actions: {
        setFetchState (fetchState: FetchState) {
            this.fetchState = fetchState;
        },
        setRecords (records: CommunicationResponse[]) {
            this.records = records;
        },
        setPreviewFetchState (fetchState: FetchState) {
            this.previewFetchState = fetchState;
        },
        setPreviewRecord (record: CommunicationResponse) {
            this.previewRecord = record;
        },
        setPartnerEmployerListFetchState (fetchState: FetchState) {
            this.partnerEmployerListFetchState = fetchState;
        },
        setPartnerEmployerList (list: PartnerEmployerList) {
            this.partnerEmployerList = list;
        },
        loadPreviewCommunication (id: number) {
            return new Promise((resolve, reject) => {
                this.setPreviewFetchState(FetchState.Loading);
                return UserService.getCommunication(id).then(resp => {
                    this.setPreviewRecord(resp.data);
                    this.setPreviewFetchState(FetchState.Loaded);
                }).catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
            });
        },
        loadEmployeeCommunications (params: { planTypes: HsabPlanType[], dashboard: CommunicationDashboard }) {
            return new Promise((resolve, reject) => {
                this.setFetchState(FetchState.Loading);
                return UserService.getEmployeeCommunications(isMobileApp(), params.planTypes, params.dashboard)
                    .then(resp => {
                        this.setRecords(resp.data);
                        this.setFetchState(FetchState.Loaded);
                    }).catch(err => {
                        return storeHelper.handleError({ error: err, rethrow: true });
                    });
            });
        },
        loadEmployerCommunications (employerId: number) {
            return new Promise((resolve, reject) => {
                this.setFetchState(FetchState.Loading);
                return UserService.getEmployerCommunications(employerId).then(resp => {
                    this.setRecords(resp.data);
                    this.setFetchState(FetchState.Loaded);
                }).catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
            });
        },
        loadPartnerEmployers (partnerId: number) {
            return new Promise((resolve, reject) => {
                this.setPartnerEmployerListFetchState(FetchState.Loading);
                return UserService.getPartnerCommunicationEmployers(partnerId).then(resp => {
                    this.setPartnerEmployerList(resp.data);
                    this.setPartnerEmployerListFetchState(FetchState.Loaded);
                }).catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
            });
        },
    }
});